import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Search, X, Icon } from '../../ui/Icon';
import type { AutocompletePrediction } from '../../../api/places.api';

export interface PickedLeg { key: string; name: string }

interface Props {
  /** ADR-0030: the destinations already added, in order. */
  legs: PickedLeg[];
  onRemove: (key: string) => void;
  value: string;
  onChange: (value: string) => void;
  predictions: AutocompletePrediction[];
  onSelect: (prediction: AutocompletePrediction) => void;
  /** Enter with text typed: that text becomes a destination. */
  onAddTyped: () => void;
  /** Enter on an empty search with destinations already added. */
  onSubmit: () => void;
  autoFocus?: boolean;
  /** False where the legs are listed elsewhere (the trip's settings). */
  showChips?: boolean;
}

export default function DestinationPicker({ legs, onRemove, value, onChange, predictions, onSelect, onAddTyped, onSubmit, autoFocus = true, showChips = true }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // The dropdown is portaled to <body>: the wizard body scrolls (overflow-y-auto),
  // which would clip an absolutely-positioned child instead of letting it float.
  useEffect(() => {
    if (predictions.length === 0 || !inputRef.current) { setPos(null); return; }
    const rect = inputRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
  }, [predictions]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (value.trim()) onAddTyped();
    else if (legs.length > 0) onSubmit();
  }

  return (
    <div className="space-y-3">
      {showChips && legs.length > 0 && (
        <ol className="flex flex-wrap gap-2">
          {legs.map((leg, i) => (
            <li key={leg.key} className="inline-flex items-center gap-1.5 pl-3 pr-1 h-9 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[14px] font-medium">
              <span className="text-[11px] font-mono text-teal-500">{i + 1}</span>
              <span className="truncate max-w-[14rem]">{leg.name}</span>
              <button
                type="button" onClick={() => onRemove(leg.key)}
                className="btn-icon w-7 h-7 text-teal-600/70 hover:text-red-500"
                aria-label={t('dashboard.newTripWizard.destinationRemove', { name: leg.name })}
              >
                <Icon as={X} size={14} />
              </button>
            </li>
          ))}
        </ol>
      )}
      <div className="relative">
        <Icon as={Search} size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={inputRef}
          autoFocus={autoFocus} type="text"
          className="input !h-14 !pl-11 !text-[17px] !rounded-2xl"
          placeholder={t(legs.length > 0 ? 'dashboard.newTripWizard.destinationAddAnother' : 'dashboard.newTripWizard.destinationPlaceholder')}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </div>
      {pos && createPortal(
        <div
          className="fixed max-h-64 overflow-y-auto bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-1 z-[9999]"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {predictions.map(p => (
            <button
              key={p.placeId} type="button"
              // preventDefault keeps the focus in the search, ready for the next destination.
              onMouseDown={e => { e.preventDefault(); onSelect(p); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
            >
              <span className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
                <Icon as={MapPin} size={15} className="text-teal-600 dark:text-teal-400" />
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-medium text-slate-900 dark:text-slate-100 truncate">{p.mainText}</span>
                <span className="block text-[12px] text-slate-400 dark:text-slate-500 truncate">{p.secondaryText}</span>
              </span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
